import { create } from 'zustand';

/**
 * Cart Store — UI/local state only.
 *
 * Server cart is the source of truth (TanStack Query, see hooks/useCart.ts).
 * This store holds:
 *   - drawer open/close
 *   - optimistic count for the navbar badge while a mutation is in flight
 *   - pending variantIds (loading state per Add-to-Cart button)
 */

interface CartUIState {
  isDrawerOpen: boolean;
  /** Optimistic count — the bracket-only display target during pending mutations.
   *  Server reconciles via syncCount() once the mutation settles. */
  optimisticCount: number;
  pendingVariantIds: number[];

  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;

  syncCount: (count: number) => void;
  bumpCount: (delta: number) => void;

  startPending: (variantId: number) => void;
  endPending: (variantId: number) => void;
  isPending: (variantId: number) => boolean;
}

export const useCartStore = create<CartUIState>()((set, get) => ({
  isDrawerOpen: false,
  optimisticCount: 0,
  pendingVariantIds: [],

  openDrawer: () => set({ isDrawerOpen: true }),
  closeDrawer: () => set({ isDrawerOpen: false }),
  toggleDrawer: () => set((s) => ({ isDrawerOpen: !s.isDrawerOpen })),

  syncCount: (count) => set({ optimisticCount: count }),
  bumpCount: (delta) =>
    set((s) => ({ optimisticCount: Math.max(0, s.optimisticCount + delta) })),

  startPending: (variantId) =>
    set((s) => ({ pendingVariantIds: [...s.pendingVariantIds, variantId] })),
  endPending: (variantId) =>
    set((s) => ({
      pendingVariantIds: s.pendingVariantIds.filter((id) => id !== variantId),
    })),
  isPending: (variantId) => get().pendingVariantIds.includes(variantId),
}));
